import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, Row, Col, Typography, Tag, Button, Space, Tabs, Timeline as AntTimeline,
  Descriptions, Avatar, message, Input, Empty, Spin, Table, Tooltip, Statistic, Divider, Progress, List,
} from 'antd';
import {
  ArrowLeftOutlined, EditOutlined, PhoneOutlined, WhatsAppOutlined, UserAddOutlined,
  StarOutlined, StarFilled, MailOutlined, EnvironmentOutlined, ClockCircleOutlined,
  FileTextOutlined, PlusOutlined, HistoryOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  fetchLeadById, toggleFavorite, convertLeadToClient, addLeadTimeline, fetchPropertyTypes,
} from '../services/leadService';
import { fetchSettings } from '../services/settingsService';
import LeadFormDrawer from './LeadFormDrawer';
import DocumentManager from '../documents/DocumentManager';
import { PRIORITY_COLORS, BRAND, colorMap, leadAgeDays, scoreColor } from './leadConstants';
import { pushRecent } from '../../utils/recentlyViewed';
import { getUser } from '../../auth/permissions';
import '../../components/RichTextEditor.css';

const { Text, Title, Paragraph } = Typography;
const { TextArea } = Input;

const fmt = (d) => (d ? dayjs(d).format('DD MMM YYYY, hh:mm A') : '—');

const typeColor = (t) => ({
  created: 'green', status_change: 'blue', priority_change: 'purple',
  followup: 'orange', note: 'cyan', document: 'geekblue', system: 'gray', edit: 'gold',
}[t] || 'blue');

const LeadDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({});
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [savingComment, setSavingComment] = useState(false);

  const priorityColors = { ...PRIORITY_COLORS, ...colorMap(settings.priority_color) };
  const me = getUser();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchLeadById(id);
      setLead(data);
      pushRecent({ id: data.LeadID, name: data.Name, sub: `#${data.LeadNo} · ${data.City || ''}` });
    }
    catch { message.error('Failed to load lead'); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    (async () => {
      try {
        const [s, pt] = await Promise.all([fetchSettings(), fetchPropertyTypes()]);
        setSettings(s); setPropertyTypes(pt);
      } catch { /* ignore */ }
    })();
  }, []);

  const handleFavorite = async () => {
    try { await toggleFavorite(id); load(); } catch { message.error('Failed'); }
  };
  const handleConvert = async () => {
    try { await convertLeadToClient(id); message.success('Converted to client'); load(); }
    catch (e) { message.error(e.response?.data?.error || 'Failed to convert'); }
  };
  const addNote = async () => {
    if (!noteText.trim()) return;
    setSavingNote(true);
    try {
      await addLeadTimeline(id, { Type: 'note', Title: 'Note added', Description: noteText.trim() });
      setNoteText(''); message.success('Note added'); load();
    } catch { message.error('Failed to add note'); }
    finally { setSavingNote(false); }
  };
  const addComment = async () => {
    if (!commentText.trim()) return;
    setSavingComment(true);
    try {
      await addLeadTimeline(id, {
        Type: 'comment', Title: `Comment by ${me?.Name || me?.Username || 'User'}`,
        Description: commentText.trim(), CreatedBy: me?.Name || me?.Username || null,
      });
      setCommentText(''); message.success('Comment added'); load();
    } catch { message.error('Failed to add comment'); }
    finally { setSavingComment(false); }
  };
  const comments = (lead?.timeline || []).filter((t) => t.Type === 'comment');

  if (loading && !lead) {
    return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>;
  }
  if (!lead) return <Empty description="Lead not found" style={{ padding: 80 }} />;

  const quickActions = (
    <Space wrap>
      <Button icon={<PhoneOutlined />} href={`tel:${lead.Mobile}`}>Call</Button>
      <Button icon={<WhatsAppOutlined style={{ color: '#25D366' }} />}
        href={`https://wa.me/${(lead.Mobile || '').replace(/[^0-9]/g, '')}`} target="_blank">WhatsApp</Button>
      <Button icon={<EditOutlined />} onClick={() => setDrawerOpen(true)}>Edit</Button>
      <Button icon={lead.IsFavorite ? <StarFilled style={{ color: '#faad14' }} /> : <StarOutlined />} onClick={handleFavorite}>
        {lead.IsFavorite ? 'Pinned' : 'Pin'}
      </Button>
      {!lead.ConvertedClientID && (
        <Button type="primary" icon={<UserAddOutlined />} onClick={handleConvert}>Convert to Client</Button>
      )}
    </Space>
  );

  const overview = (
    <Row gutter={[16, 16]}>
      <Col xs={24} md={16}>
        <Card title="Customer Summary" bordered={false} style={{ borderRadius: 8 }}>
          <Descriptions
            bordered size="small"
            column={{ xs: 1, sm: 2, md: 2, lg: 2 }}
            labelStyle={{ width: 150, color: '#8c8c8c', background: '#fafafa' }}
            contentStyle={{ background: '#fff' }}
          >
            <Descriptions.Item label="Name">{lead.Name}</Descriptions.Item>
            <Descriptions.Item label="Lead No">#{lead.LeadNo}</Descriptions.Item>
            <Descriptions.Item label="Mobile"><PhoneOutlined /> {lead.Mobile}</Descriptions.Item>
            <Descriptions.Item label="Alternate">{lead.AltMobile || '—'}</Descriptions.Item>
            <Descriptions.Item label="Email"><MailOutlined /> {lead.Email || '—'}</Descriptions.Item>
            <Descriptions.Item label="City"><EnvironmentOutlined /> {lead.City || '—'}</Descriptions.Item>
            <Descriptions.Item label="Property Type">{lead.PropertyType || '—'}</Descriptions.Item>
            <Descriptions.Item label="Source">{lead.Source ? <Tag>{lead.Source}</Tag> : '—'}</Descriptions.Item>
            <Descriptions.Item label="Current Situation">{lead.CurrentSituation || '—'}</Descriptions.Item>
            <Descriptions.Item label="Expected Start">
              {lead.ExpectedStart === 'After Some Time'
                ? `After ${lead.ExpectedStartValue || ''} ${lead.ExpectedStartUnit || ''}`
                : (lead.ExpectedStart || '—')}
            </Descriptions.Item>
            <Descriptions.Item label="Assigned User">{lead.AssignedUser || '—'}</Descriptions.Item>
            <Descriptions.Item label="Next Follow-up">{fmt(lead.NextFollowupAt)}</Descriptions.Item>
            <Descriptions.Item label="Address" span={2}>{lead.Address || '—'}</Descriptions.Item>
          </Descriptions>
          {lead.Tags && lead.Tags.length > 0 && (
            <>
              <Divider plain orientation="left" style={{ margin: '12px 0' }}>Tags</Divider>
              <Space wrap>
                {lead.Tags.map(t => {
                  const preset = (settings.lead_tag || []).find(x => x.Value === t);
                  return <Tag key={t} color={preset ? preset.Color : 'default'}>{t}</Tag>;
                })}
              </Space>
            </>
          )}
          {lead.Notes && (
            <>
              <Divider plain orientation="left" style={{ margin: '12px 0' }}>Notes</Divider>
              <div className="rte-render"
                dangerouslySetInnerHTML={{ __html: String(lead.Notes).replace(/<script[\s\S]*?<\/script>/gi, '') }} />
            </>
          )}
        </Card>
      </Col>
      <Col xs={24} md={8}>
        <Card bordered={false} style={{ borderRadius: 8, marginBottom: 16 }}>
          <div style={{ textAlign: 'center' }}>
            <Progress type="dashboard" percent={lead.LeadScore} size={120}
              strokeColor={scoreColor(lead.LeadScore)} format={(p) => <span style={{ fontSize: 22 }}>{p}</span>} />
            <div><Text type="secondary">Lead Score</Text></div>
          </div>
        </Card>
        <Card bordered={false} style={{ borderRadius: 8 }}>
          <Row gutter={16}>
            <Col span={12}><Statistic title="Age" value={leadAgeDays(lead.createdAt)} suffix="days" valueStyle={{ fontSize: 18 }} /></Col>
            <Col span={12}><Statistic title="Follow-ups" value={(lead.followups || []).length} valueStyle={{ fontSize: 18 }} /></Col>
          </Row>
          <Divider style={{ margin: '12px 0' }} />
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            <div><Text type="secondary">Created: </Text>{fmt(lead.createdAt)}</div>
            <div><Text type="secondary">Updated: </Text>{fmt(lead.updatedAt)}</div>
          </Space>
        </Card>
      </Col>
    </Row>
  );

  const timelineTab = (
    <Card bordered={false} style={{ borderRadius: 8 }}>
      <Space.Compact style={{ width: '100%', marginBottom: 20 }}>
        <Input placeholder="Add a quick note to the timeline..." value={noteText}
          onChange={(e) => setNoteText(e.target.value)} onPressEnter={addNote} />
        <Button type="primary" icon={<PlusOutlined />} loading={savingNote} onClick={addNote}>Add</Button>
      </Space.Compact>
      {(lead.timeline || []).length === 0 ? (
        <Empty description="No activity yet" />
      ) : (
        <AntTimeline
          items={(lead.timeline || []).map(t => ({
            color: typeColor(t.Type),
            children: (
              <div>
                <Text strong>{t.Title}</Text>
                {t.Description && <div><Text type="secondary">{t.Description}</Text></div>}
                <div><Text type="secondary" style={{ fontSize: 12 }}><ClockCircleOutlined /> {fmt(t.createdAt)}</Text></div>
              </div>
            ),
          }))}
        />
      )}
    </Card>
  );

  const followupsTab = (
    <Card bordered={false} style={{ borderRadius: 8 }}
      title="Follow-ups"
      extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => navigate(`/calls/new?leadId=${id}`)}>Add Follow-up</Button>}>
      <Table
        size="small" bordered rowKey="FollowupID"
        dataSource={lead.followups || []}
        locale={{ emptyText: <Empty description="No follow-ups yet" /> }}
        pagination={false}
        columns={[
          { title: 'Date', dataIndex: 'createdAt', render: fmt, width: 170 },
          { title: 'Call Status', dataIndex: 'CallStatus', render: (t) => t ? <Tag>{t}</Tag> : '—', width: 120 },
          { title: 'Outcome', dataIndex: 'Outcome', render: (t) => t || '—' },
          { title: 'Next', dataIndex: 'ScheduledAt', render: fmt, width: 170 },
          { title: 'Notes', dataIndex: 'Notes', render: (t) => t || '—' },
        ]}
      />
    </Card>
  );

  const documentsTab = (
    <Card bordered={false} style={{ borderRadius: 8 }} title="Documents">
      <DocumentManager leadId={id} entityType="Lead" settings={settings} onChanged={load} />
    </Card>
  );

  const activityTab = (
    <Card bordered={false} style={{ borderRadius: 8 }} title="Activity Log">
      <Table
        size="small" bordered rowKey="TimelineID"
        dataSource={lead.timeline || []}
        locale={{ emptyText: <Empty description="No activity" /> }}
        pagination={{ pageSize: 15 }}
        columns={[
          { title: 'When', dataIndex: 'createdAt', render: fmt, width: 180 },
          { title: 'Type', dataIndex: 'Type', width: 140, render: (t) => <Tag color={typeColor(t)}>{t}</Tag> },
          { title: 'Activity', dataIndex: 'Title' },
          { title: 'Details', dataIndex: 'Description', render: (t) => t || '—' },
        ]}
      />
    </Card>
  );

  const commentsTab = (
    <Card bordered={false} style={{ borderRadius: 8 }} title="Internal Comments">
      <Space.Compact style={{ width: '100%', marginBottom: 16 }}>
        <Input placeholder="Add an internal comment for your team…" value={commentText}
          onChange={(e) => setCommentText(e.target.value)} onPressEnter={addComment} />
        <Button type="primary" icon={<PlusOutlined />} loading={savingComment} onClick={addComment}>Comment</Button>
      </Space.Compact>
      {comments.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No comments yet" /> : (
        <List
          dataSource={comments}
          renderItem={(c) => (
            <List.Item>
              <List.Item.Meta
                avatar={<Avatar style={{ backgroundColor: BRAND }}>{(c.CreatedBy || 'U').charAt(0).toUpperCase()}</Avatar>}
                title={<Space><Text strong>{c.CreatedBy || 'User'}</Text><Text type="secondary" style={{ fontSize: 12 }}>{fmt(c.createdAt)}</Text></Space>}
                description={<Text>{c.Description}</Text>}
              />
            </List.Item>
          )}
        />
      )}
    </Card>
  );

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16, padding: '4px 8px' }} gutter={[8, 8]}>
        <Col>
          <Space align="center">
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/leads')} />
            <Avatar size={44} style={{ backgroundColor: priorityColors[lead.Priority] || BRAND }}>
              {lead.Name ? lead.Name.charAt(0).toUpperCase() : '?'}
            </Avatar>
            <div>
              <Title level={4} style={{ margin: 0 }}>
                {lead.Name}{' '}
                <Tag color={priorityColors[lead.Priority] || 'default'} style={{ borderRadius: 12 }}>{lead.Priority}</Tag>
                {lead.ConvertedClientID && <Tag color="green">Client</Tag>}
              </Title>
              <Text type="secondary">#{lead.LeadNo} · {lead.Status} · {lead.City || 'No city'}</Text>
            </div>
          </Space>
        </Col>
        <Col>{quickActions}</Col>
      </Row>

      <Tabs
        defaultActiveKey="overview"
        items={[
          { key: 'overview', label: 'Overview', children: overview },
          { key: 'timeline', label: <span><HistoryOutlined /> Timeline</span>, children: timelineTab },
          { key: 'followups', label: 'Follow-ups', children: followupsTab },
          { key: 'documents', label: 'Documents', children: documentsTab },
          { key: 'comments', label: <span>Comments{comments.length ? ` (${comments.length})` : ''}</span>, children: commentsTab },
          { key: 'activity', label: 'Activity Log', children: activityTab },
        ]}
      />

      <LeadFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSaved={() => { setDrawerOpen(false); load(); }}
        editingLead={lead}
        settings={settings}
        propertyTypes={propertyTypes}
      />
    </div>
  );
};

export default LeadDetail;
